import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import { showApiError } from '../../utils/errorUtils';
import './WarehouseDashboard.css';
import { formatAppDate } from '../../utils/dateUtils';

const ShippingOperations = forwardRef(({ activeSubTab = 'picking' }, ref) => {
  const { t } = useTranslation('warehouse');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useMessages();
  const statusLabel = (code) => (code ? t(`shippingOperations.status.${code}`, { defaultValue: code }) : code);

  // Pick lists states
  const [pickLists, setPickLists] = useState([]);
  const [loadingPickLists, setLoadingPickLists] = useState(false);
  const [selectedPickList, setSelectedPickList] = useState(null);
  const [pickListDetails, setPickListDetails] = useState(null);
  const [showPickDetailsModal, setShowPickDetailsModal] = useState(false);
  const [loadingPickDetails, setLoadingPickDetails] = useState(false);

  // Packing states
  const [packingItems, setPackingItems] = useState([]);
  const [loadingPacking, setLoadingPacking] = useState(false);
  const [selectedPackingItem, setSelectedPackingItem] = useState(null);
  const [showPackingModal, setShowPackingModal] = useState(false);
  const [submittingPacking, setSubmittingPacking] = useState(false);
  const [startingPickList, setStartingPickList] = useState(false);
  const [completingPickList, setCompletingPickList] = useState(false);

  // Sales Order Packing states (new sales-order-centric approach)
  const [packingTab, setPackingTab] = useState('sales-orders');
  const [salesOrderPackingItems, setSalesOrderPackingItems] = useState([]);
  const [loadingSalesOrderPacking, setLoadingSalesOrderPacking] = useState(false);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState(null);
  const [showSalesOrderPackingModal, setShowSalesOrderPackingModal] = useState(false);

  // Sales Order Shipping states
  const [shippingTab, setShippingTab] = useState('ready-to-ship');
  const [readyToShipOrders, setReadyToShipOrders] = useState([]);
  const [shippedOrders, setShippedOrders] = useState([]);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [selectedShippingOrder, setSelectedShippingOrder] = useState(null);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [submittingShipping, setSubmittingShipping] = useState(false);
  const [shippingTrackingNumber, setShippingTrackingNumber] = useState('');
  const [shippingCarrier, setShippingCarrier] = useState('');
  const [shippingNotes, setShippingNotes] = useState('');

  // ─── Fetch functions ──────────────────────────────────────────────

  const fetchPickLists = async () => {
    try {
      setLoadingPickLists(true);
      const response = await apiFetch(getApiUrl('/api/pick-lists?limit=100'));
      if (response.ok) {
        const data = await response.json();
        setPickLists(data || []);
      }
    } catch (error) {
      console.error('Error fetching pick lists:', error);
      setPickLists([]);
    } finally {
      setLoadingPickLists(false);
    }
  };

  const fetchPackingItems = async () => {
    try {
      setLoadingPacking(true);
      // Fetch completed pick lists that are ready for packing - add timestamp to prevent caching
      const timestamp = Date.now();
      const response = await apiFetch(getApiUrl(`/api/pick-lists?status=COMPLETED&limit=100&_t=${timestamp}`));
      if (response.ok) {
        const data = await response.json();
        setPackingItems(data || []);
      }
    } catch (error) {
      console.error('Error fetching packing items:', error);
      setPackingItems([]);
    } finally {
      setLoadingPacking(false);
    }
  };

  // Sales Order Packing fetch function (new sales-order-centric approach)
  const fetchSalesOrderPackingItems = async () => {
    try {
      setLoadingSalesOrderPacking(true);
      const response = await apiFetch(getApiUrl('/api/sales-orders/packing-ready'));
      if (response.ok) {
        const data = await response.json();
        setSalesOrderPackingItems(data.orders || []);
      } else {
        console.error('Failed to fetch sales order packing items');
        setSalesOrderPackingItems([]);
      }
    } catch (error) {
      console.error('Error fetching sales order packing items:', error);
      setSalesOrderPackingItems([]);
    } finally {
      setLoadingSalesOrderPacking(false);
    }
  };

  // Shipping functions
  const fetchShippingOrders = async () => {
    setLoadingShipping(true);
    try {
      // Fetch ready to ship orders (PACKED status)
      const readyResponse = await apiFetch(getApiUrl('/api/sales-orders/?status=PACKED&page_size=100'));
      if (readyResponse.ok) {
        const readyData = await readyResponse.json();
        setReadyToShipOrders(readyData.orders || []);
      }

      // Fetch shipped orders
      const shippedResponse = await apiFetch(getApiUrl('/api/sales-orders/?status=SHIPPED&page_size=100'));
      if (shippedResponse.ok) {
        const shippedData = await shippedResponse.json();
        setShippedOrders(shippedData.orders || []);
      }
    } catch (error) {
      console.error('Error fetching shipping orders:', error);
      setReadyToShipOrders([]);
      setShippedOrders([]);
    } finally {
      setLoadingShipping(false);
    }
  };

  const fetchPickListDetails = async (pickListId) => {
    try {
      setLoadingPickDetails(true);
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${pickListId}`));
      if (response.ok) {
        const data = await response.json();
        setPickListDetails(data);
      }
    } catch (error) {
      console.error('Error fetching pick list details:', error);
      setPickListDetails(null);
    } finally {
      setLoadingPickDetails(false);
    }
  };

  // ─── Data loading based on active sub-tab ─────────────────────────

  useEffect(() => {
    if (activeSubTab === 'picking') {
      fetchPickLists();
    } else if (activeSubTab === 'packing') {
      fetchSalesOrderPackingItems();
      fetchPackingItems();
    } else if (activeSubTab === 'shipping') {
      fetchShippingOrders();
    }
  }, [activeSubTab]);

  // ─── Expose refresh method to parent ──────────────────────────────

  useImperativeHandle(ref, () => ({
    refresh: () => {
      if (activeSubTab === 'picking') {
        fetchPickLists();
      } else if (activeSubTab === 'packing') {
        fetchSalesOrderPackingItems();
        fetchPackingItems();
      } else if (activeSubTab === 'shipping') {
        fetchShippingOrders();
      }
    }
  }));

  // ─── Shipping handlers ────────────────────────────────────────────

  const handleStartShipping = (order) => {
    setSelectedShippingOrder(order);
    setShippingTrackingNumber('');
    setShippingCarrier('');
    setShippingNotes('');
    setShowShippingModal(true);
  };

  const handleCloseShippingModal = () => {
    setShowShippingModal(false);
    setSelectedShippingOrder(null);
    setShippingTrackingNumber('');
    setShippingCarrier('');
    setShippingNotes('');
  };

  const handleShipOrder = async () => {
    if (!selectedShippingOrder || !user?.user_id) return;

    setSubmittingShipping(true);
    try {
      const queryParams = new URLSearchParams({
        shipped_by: user.user_id.toString()
      });

      if (shippingTrackingNumber) queryParams.append('tracking_number', shippingTrackingNumber);
      if (shippingCarrier) queryParams.append('carrier', shippingCarrier);
      if (shippingNotes) queryParams.append('notes', shippingNotes);

      const response = await apiFetch(getApiUrl(`/api/sales-orders/${selectedShippingOrder.sales_order_id}/ship?${queryParams}`), {
        method: 'PUT'
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.shipping.orderShipped'), result.message);
        handleCloseShippingModal();
        fetchShippingOrders(); // Refresh the shipping data
      } else {
        // The API answers {error_code, message}: translated like everywhere else (#383)
        await showApiError(response, showError, tCommon, t('errors.shipping.shippingFailed'));
      }
    } catch (error) {
      console.error('Shipping error:', error);
      showError(t('errors.generic.error'), t('errors.shipping.shippingError'));
    } finally {
      setSubmittingShipping(false);
    }
  };

  // ─── Packing handlers ─────────────────────────────────────────────

  const handleStartPacking = (packingItem) => {
    setSelectedPackingItem(packingItem);
    setShowPackingModal(true);
  };


  const handleClosePackingModal = () => {
    setShowPackingModal(false);
    setSelectedPackingItem(null);
    // Refresh packing items after modal closes
    if (activeSubTab === 'packing') {
      fetchPackingItems();
    }
  };

  const handleMarkAsPacked = async () => {
    if (!selectedPackingItem) return;

    try {
      setSubmittingPacking(true);
      const notes = 'Packed via warehouse packing interface';
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${selectedPackingItem.pick_list_id}/pack?packed_by=${user.user_id}&notes=${encodeURIComponent(notes)}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.shipping.packingComplete'), result.message);
        handleClosePackingModal();
      } else {
        await showApiError(response, showError, tCommon, t('errors.shipping.packingFailed'));
      }
    } catch (error) {
      console.error('Packing error:', error);
      showError(t('errors.generic.error'), t('errors.shipping.packingError'));
    } finally {
      setSubmittingPacking(false);
    }
  };


  // Sales Order Packing handlers (new sales-order-centric approach)
  const handleStartSalesOrderPacking = (salesOrder) => {
    setSelectedSalesOrder(salesOrder);
    setShowSalesOrderPackingModal(true);
  };

  const handleCloseSalesOrderPackingModal = () => {
    setShowSalesOrderPackingModal(false);
    setSelectedSalesOrder(null);
    // Refresh sales order packing items after modal closes
    if (activeSubTab === 'packing' && packingTab === 'sales-orders') {
      fetchSalesOrderPackingItems();
    }
  };

  const handlePackSalesOrder = async () => {
    if (!selectedSalesOrder) return;

    try {
      setSubmittingPacking(true);
      const notes = 'Packed via warehouse sales order packing interface';
      const response = await apiFetch(getApiUrl(`/api/sales-orders/${selectedSalesOrder.sales_order_id}/pack?packed_by=${user.user_id}&notes=${encodeURIComponent(notes)}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.shipping.salesOrderPacked'), result.message);
        handleCloseSalesOrderPackingModal();
      } else {
        await showApiError(response, showError, tCommon, t('errors.shipping.packingFailed'));
      }
    } catch (error) {
      console.error('Sales order packing error:', error);
      showError(t('errors.generic.error'), t('errors.shipping.salesOrderPackingError'));
    } finally {
      setSubmittingPacking(false);
    }
  };

  // ─── Pick List handlers ───────────────────────────────────────────

  const handleViewPickList = async (pickList) => {
    setSelectedPickList(pickList);
    setShowPickDetailsModal(true);
    await fetchPickListDetails(pickList.pick_list_id);
  };

  const closePickDetailsModal = () => {
    setShowPickDetailsModal(false);
    setSelectedPickList(null);
    setPickListDetails(null);
  };

  const handleStartPickList = async (pickList) => {
    try {
      setStartingPickList(true);
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${pickList.pick_list_id}/start`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.user_id
        })
      });

      if (response.ok) {
        showSuccess(t('success.pickLists.pickListStarted'), t('success.pickLists.pickListStartedMessage', { pickListNumber: pickList.pick_list_number }));
        await fetchPickLists();
        if (selectedPickList && selectedPickList.pick_list_id === pickList.pick_list_id) {
          await fetchPickListDetails(pickList.pick_list_id);
          setSelectedPickList({ ...pickList, status: 'IN_PROGRESS' });
        }
      } else {
        const error = await response.json();
        let errorMessage = t('errors.generic.unknownError');

        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          errorMessage = error.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        } else if (error.detail && typeof error.detail === 'object' && error.detail.msg) {
          errorMessage = error.detail.msg;
        }

        showError(t('errors.pickLists.failedToStartPickList'), errorMessage);
      }
    } catch (error) {
      console.error('Error starting pick list:', error);
      showError(t('errors.pickLists.failedToStartPickList'), t('errors.pickLists.failedToStartPickListNetwork'));
    } finally {
      setStartingPickList(false);
    }
  };

  // Streamlined inline pick recording
  const handleInlinePickRecord = async (item, quantity) => {
    if (quantity === null || quantity === undefined || isNaN(parseFloat(quantity)) || parseFloat(quantity) < 0) {
      showWarning(t('shippingOperations.toasts.invalidQuantity'), t('shippingOperations.toasts.enterValidQuantity'));
      return false;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${selectedPickList.pick_list_id}/items/${item.pick_item_id}/pick`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          picked_quantity: parseFloat(quantity),
          picked_by: user.user_id,
          notes: null
        })
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.pickLists.pickUpdated'), t('success.pickLists.pickUpdatedMessage', { itemCode: item.item_code, pickedQuantity: result.picked_quantity, status: result.status }));
        await fetchPickListDetails(selectedPickList.pick_list_id);
        return true;
      } else {
        const error = await response.json();
        showError(t('errors.pickLists.failedToRecordPick'), error.detail || t('errors.generic.unknownError'));
        return false;
      }
    } catch (error) {
      console.error('Error recording pick:', error);
      showError(t('errors.pickLists.failedToRecordPick'), t('errors.pickLists.failedToRecordPickNetwork'));
      return false;
    }
  };

  // Quick pick actions
  const handleQuickPick = async (item) => {
    await handleInlinePickRecord(item, item.planned_quantity);
  };

  const handleZeroPick = async (item) => {
    await handleInlinePickRecord(item, 0);
  };

  const handleCompletePickList = async () => {
    try {
      setCompletingPickList(true);
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${selectedPickList.pick_list_id}/complete`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.user_id
        })
      });

      if (response.ok) {
        const result = await response.json();
        const statusBreakdown = result.short_items > 0
          ? t('shippingOperations.toasts.pickBreakdownWithShort', { total: result.total_items, picked: result.picked_items, short: result.short_items })
          : t('shippingOperations.toasts.pickBreakdown', { total: result.total_items, picked: result.picked_items });
        showSuccess(t('success.pickLists.pickListCompleted'), t('success.pickLists.pickListCompletedMessage', { message: result.message, statusBreakdown }));
        closePickDetailsModal();
        await fetchPickLists();
      } else {
        await showApiError(response, showError, tCommon, t('errors.pickLists.failedToCompletePickList'));
      }
    } catch (error) {
      console.error('Error completing pick list:', error);
      showError(t('errors.pickLists.failedToCompletePickList'), t('errors.pickLists.failedToCompletePickListNetwork'));
    } finally {
      setCompletingPickList(false);
    }
  };

  const handleIssuePickList = async (pickList, deliveryNotes = '') => {
    try {
      setCompletingPickList(true);
      const response = await apiFetch(getApiUrl(`/api/pick-lists/${pickList.pick_list_id}/issue`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.user_id,
          delivery_notes: deliveryNotes || 'Issued to production floor'
        })
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(t('success.pickLists.materialsIssued'), t('success.pickLists.materialsIssuedMessage', { pickListNumber: pickList.pick_list_number }));
        await fetchPickLists();
      } else {
        const error = await response.json();
        let errorMessage = t('errors.generic.unknownError');

        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          // Pydantic validation error - extract first error message
          errorMessage = error.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        } else if (error.detail && typeof error.detail === 'object' && error.detail.msg) {
          errorMessage = error.detail.msg;
        } else if (typeof error.message === 'string') {
          errorMessage = error.message;
        }

        showError(t('errors.pickLists.failedToIssueMaterials'), errorMessage);
      }
    } catch (error) {
      console.error('Error issuing pick list:', error);
      showError(t('errors.pickLists.failedToIssueMaterials'), t('errors.pickLists.failedToIssueMaterialsNetwork'));
    } finally {
      setCompletingPickList(false);
    }
  };

  // ─── Render: Pick Lists ───────────────────────────────────────────

  const renderPickLists = () => (
    <div className="warehouse-section">
      {loadingPickLists ? (
        <div className="loading-state">{t('shippingOperations.loading.pickLists')}</div>
      ) : (
        <div className="warehouse-table-container">
          <table className="warehouse-table">
            <thead>
              <tr>
                <th>{t('shippingOperations.columns.pickListNumber')}</th>
                <th>{t('shippingOperations.columns.orderReference')}</th>
                <th>{tCommon('ui.customer')}</th>
                <th>{tCommon('ui.type')}</th>
                <th>{tCommon('ui.priority')}</th>
                <th>{tCommon('ui.status')}</th>
                <th>{t('shippingOperations.columns.assignedTo')}</th>
                <th>{t('shippingOperations.columns.created')}</th>
                <th>{tCommon('ui.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {pickLists.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    {t('shippingOperations.empty.noPickLists')}
                  </td>
                </tr>
              ) : (
                pickLists.map(pickList => {
                  let statusClass = 'status-pending';
                  if (pickList.status === 'IN_PROGRESS') statusClass = 'status-in-progress';
                  if (pickList.status === 'COMPLETED') statusClass = 'status-success';
                  if (pickList.status === 'CANCELLED') statusClass = 'status-error';

                  let priorityClass = 'status-secondary';
                  if (pickList.priority === 'HIGH') priorityClass = 'status-warning';
                  if (pickList.priority === 'URGENT') priorityClass = 'status-error';

                  return (
                    <tr key={pickList.pick_list_id}>
                      <td><strong>{pickList.pick_list_number}</strong></td>
                      <td>{pickList.po_number || pickList.so_number || '-'}</td>
                      <td>{pickList.customer_name || '-'}</td>
                      <td>{pickList.pick_list_type}</td>
                      <td><span className={`status-badge ${priorityClass}`}>{statusLabel(pickList.priority)}</span></td>
                      <td><span className={`status-badge ${statusClass}`}>{statusLabel(pickList.status)}</span></td>
                      <td>{pickList.assigned_to_name || '-'}</td>
                      <td>{formatAppDate(pickList.created_date)}</td>
                      <td>
                        {pickList.status === 'PENDING' && (
                          <button className="btn-action btn-success" onClick={() => handleStartPickList(pickList)} disabled={startingPickList} style={{ marginRight: '0.5rem' }}>
                            ▶️ {t('common.buttons.start')}
                          </button>
                        )}
                        {pickList.status === 'COMPLETED' && (!pickList.issued_date && !pickList.issued_by) && pickList.pick_list_type !== 'SALES_ORDER' && (
                          <button className="btn-action btn-warning" onClick={() => handleIssuePickList(pickList)} disabled={completingPickList} style={{ marginRight: '0.5rem' }}>
                            🚚 {t('shippingOperations.buttons.issue')}
                          </button>
                        )}
                        <button className="btn-action btn-primary" onClick={() => handleViewPickList(pickList)}>
                          📋 {t('shippingOperations.buttons.details')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ─── Render: Packing ──────────────────────────────────────────────

  const renderPacking = () => (
    <div className="warehouse-section">
      {/* Packing Tab Navigation */}
      <div className="packing-tabs" style={{
        display: 'flex',
        borderBottom: '2px solid var(--border-color)',
        marginBottom: '1rem'
      }}>
        <button
          className={`packing-tab ${packingTab === 'sales-orders' ? 'active' : ''}`}
          onClick={() => setPackingTab('sales-orders')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: packingTab === 'sales-orders' ? 'var(--primary-color)' : 'var(--background-secondary)',
            color: packingTab === 'sales-orders' ? 'white' : 'var(--text-primary)',
            cursor: 'pointer',
            borderRadius: '4px 4px 0 0',
            fontWeight: packingTab === 'sales-orders' ? 'bold' : 'normal',
            marginRight: '0.25rem'
          }}
        >
          🚚 {t('shippingOperations.tabs.salesOrders')}
        </button>
        <button
          className={`packing-tab ${packingTab === 'requisitions' ? 'active' : ''}`}
          onClick={() => setPackingTab('requisitions')}
          style={{
            padding: '0.75rem 1.5rem',
            border: 'none',
            background: packingTab === 'requisitions' ? 'var(--primary-color)' : 'var(--background-secondary)',
            color: packingTab === 'requisitions' ? 'white' : 'var(--text-primary)',
            cursor: 'pointer',
            borderRadius: '4px 4px 0 0',
            fontWeight: packingTab === 'requisitions' ? 'bold' : 'normal'
          }}
        >
          📋 {t('shippingOperations.tabs.requisitions')}
        </button>
      </div>

      {/* Tab Content */}
      {packingTab === 'sales-orders' ? (
        // Sales Orders Tab - New sales-order-centric interface
        <div className="sales-order-packing-section">
          {loadingSalesOrderPacking ? (
            <div className="loading-state">{t('shippingOperations.loading.salesOrdersPacking')}</div>
          ) : (
            <div className="warehouse-table-container">
              <table className="warehouse-table">
                <thead>
                  <tr>
                    <th>{t('shippingOperations.columns.salesOrderNumber')}</th>
                    <th>{tCommon('ui.customer')}</th>
                    <th>{t('shippingOperations.columns.pickListsStatus')}</th>
                    <th>{t('shippingOperations.columns.completionStatus')}</th>
                    <th>{t('shippingOperations.columns.estValue')}</th>
                    <th>{tCommon('ui.priority')}</th>
                    <th>{tCommon('ui.status')}</th>
                    <th>{tCommon('ui.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {salesOrderPackingItems.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                        {t('shippingOperations.empty.noSalesOrdersPacking')}
                        <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                          {t('shippingOperations.empty.noSalesOrdersPackingHint')}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    salesOrderPackingItems.map(salesOrder => {
                      const completedPickLists = (salesOrder.direct_completed || 0) + (salesOrder.production_completed || 0);
                      const totalPickLists = salesOrder.total_pick_lists || 0;
                      const isFullyReady = totalPickLists > 0 && completedPickLists === totalPickLists;
                      const isPartiallyReady = completedPickLists > 0 && completedPickLists < totalPickLists;
                      const isPacked = salesOrder.so_status === 'PACKED';

                      let statusText = t('qualityStatuses.pending');
                      let statusClass = 'status-warning';
                      if (isPacked) {
                        statusText = statusLabel('PACKED');
                        statusClass = 'status-success';
                      } else if (isFullyReady) {
                        statusText = t('shippingOperations.status.READY_TO_PACK');
                        statusClass = 'status-warning';
                      } else if (isPartiallyReady) {
                        statusText = statusLabel('PARTIAL');
                        statusClass = 'status-inactive';
                      }

                      return (
                        <tr key={salesOrder.sales_order_id}>
                          <td><strong>{salesOrder.so_number}</strong></td>
                          <td>{salesOrder.customer_name || '-'}</td>
                          <td>
                            <span style={{ fontSize: '0.9em' }}>
                              {t('shippingOperations.labels.completedOfTotal', { completed: completedPickLists, total: totalPickLists })}
                              {salesOrder.direct_pick_lists > 0 && (
                                <div style={{ fontSize: '0.8em', color: 'var(--text-secondary)' }}>
                                  {t('shippingOperations.labels.direct')}: {salesOrder.direct_completed}/{salesOrder.direct_pick_lists}
                                </div>
                              )}
                              {salesOrder.production_pick_lists > 0 && (
                                <div style={{ fontSize: '0.8em', color: 'var(--text-secondary)' }}>
                                  {t('shippingOperations.labels.production')}: {salesOrder.production_completed}/{salesOrder.production_pick_lists}
                                </div>
                              )}
                            </span>
                          </td>
                          <td>
                            <div style={{
                              width: '100px',
                              height: '6px',
                              background: 'var(--border-color)',
                              borderRadius: '3px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${totalPickLists > 0 ? (completedPickLists / totalPickLists) * 100 : 0}%`,
                                height: '100%',
                                background: isFullyReady ? 'var(--success)' : 'var(--warning)',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                            <span style={{ fontSize: '0.8em', marginLeft: '0.5rem' }}>
                              {totalPickLists > 0 ? Math.round((completedPickLists / totalPickLists) * 100) : 0}%
                            </span>
                          </td>
                          <td>${salesOrder.total_value ? parseFloat(salesOrder.total_value).toFixed(2) : '0.00'}</td>
                          <td>
                            <span className={`status-badge ${
                              salesOrder.priority === 'HIGH' ? 'status-error' :
                              salesOrder.priority === 'MEDIUM' ? 'status-warning' : 'status-inactive'
                            }`}>
                              {statusLabel(salesOrder.priority || 'NORMAL')}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${statusClass}`}>
                              {statusText}
                            </span>
                          </td>
                          <td>
                            {isFullyReady && !isPacked ? (
                              <button
                                className="btn-action btn-primary"
                                onClick={() => handleStartSalesOrderPacking(salesOrder)}
                              >
                                📦 {t('shippingOperations.buttons.packOrder')}
                              </button>
                            ) : isPacked ? (
                              <button
                                className="btn-action btn-secondary"
                                onClick={() => handleStartSalesOrderPacking(salesOrder)}
                              >
                                👁️ {t('shippingOperations.buttons.viewDetails')}
                              </button>
                            ) : (
                              <button
                                className="btn-action btn-secondary"
                                onClick={() => handleStartSalesOrderPacking(salesOrder)}
                                disabled={totalPickLists === 0}
                              >
                                👁️ {t('shippingOperations.buttons.viewStatus')}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        // Requisitions Tab - Existing pick-list interface for requisition-based pick lists
        <div className="requisition-packing-section">
          {loadingPacking ? (
            <div className="loading-state">{t('shippingOperations.loading.requisitionPickLists')}</div>
          ) : (
            <div className="warehouse-table-container">
              <table className="warehouse-table">
                <thead>
                  <tr>
                    <th>{t('shippingOperations.columns.pickListNumber')}</th>
                    <th>{tCommon('ui.type')}</th>
                    <th>{t('shippingOperations.columns.productionOrder')}</th>
                    <th>{t('shippingOperations.columns.completedDate')}</th>
                    <th>{t('shippingOperations.columns.items')}</th>
                    <th>{tCommon('ui.status')}</th>
                    <th>{tCommon('ui.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {packingItems.filter(item => item.pick_list_type === 'REQUISITION' || item.production_order_id).length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                        {t('shippingOperations.empty.noRequisitionPickLists')}
                        <p style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                          {t('shippingOperations.empty.noRequisitionPickListsHint')}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    packingItems
                      .filter(item => item.pick_list_type === 'REQUISITION' || item.production_order_id)
                      .map(item => {
                        const isPackable = item.status === 'COMPLETED' && !item.packed_date;

                        return (
                          <tr key={item.pick_list_id}>
                            <td><strong>{item.pick_list_number}</strong></td>
                            <td>{item.pick_list_type}</td>
                            <td>{item.po_number || '-'}</td>
                            <td>{item.completion_time ? formatAppDate(item.completion_time) : '-'}</td>
                            <td>{t('shippingOperations.labels.itemsCount', { count: item.total_items || 0 })}</td>
                            <td>
                              <span className={`status-badge ${
                                item.packed_date ? 'status-success' : 'status-warning'
                              }`}>
                                {item.packed_date ? statusLabel('PACKED') : t('shippingOperations.status.READY_TO_PACK')}
                              </span>
                            </td>
                            <td>
                              {isPackable ? (
                                <button
                                  className="btn-action btn-primary"
                                  onClick={() => handleStartPacking(item)}
                                >
                                  📦 {t('shippingOperations.buttons.pack')}
                                </button>
                              ) : (
                                <button
                                  className="btn-action btn-secondary"
                                  onClick={() => handleStartPacking(item)}
                                >
                                  👁️ {t('shippingOperations.buttons.viewDetails')}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ─── Render: Shipping ─────────────────────────────────────────────

  const renderSalesOrderShipping = () => (
    <div className="warehouse-section">
      <div className="section-header">
        <h3>{t('shippingOperations.shipping.title')}</h3>
        <button className="btn-primary" onClick={fetchShippingOrders}>
          <i className="icon">🔄</i> {tCommon('ui.refresh')}
        </button>
      </div>

      {/* Shipping Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${shippingTab === 'ready-to-ship' ? 'active' : ''}`}
            onClick={() => setShippingTab('ready-to-ship')}
          >
            📦 {t('shippingOperations.tabs.readyToShip')} ({readyToShipOrders.length})
          </button>
          <button
            className={`tab ${shippingTab === 'shipped' ? 'active' : ''}`}
            onClick={() => setShippingTab('shipped')}
          >
            🚢 {t('shippingOperations.tabs.shippedOrders')} ({shippedOrders.length})
          </button>
        </div>
      </div>

      {loadingShipping ? (
        <div className="loading-state">{t('shippingOperations.loading.shipping')}</div>
      ) : (
        <div className="tab-content">
          {shippingTab === 'ready-to-ship' && (
            <div className="shipping-list">
              <div className="list-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('shippingOperations.columns.orderNumber')}</th>
                      <th>{tCommon('ui.customer')}</th>
                      <th>{t('shippingOperations.columns.orderDate')}</th>
                      <th>{tCommon('ui.total')}</th>
                      <th>{tCommon('ui.status')}</th>
                      <th>{tCommon('ui.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readyToShipOrders.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="no-data">
                          {t('shippingOperations.empty.noReadyToShip')}
                        </td>
                      </tr>
                    ) : (
                      readyToShipOrders.map(order => (
                        <tr key={order.sales_order_id}>
                          <td className="customer-code">
                            <code>{order.so_number}</code>
                          </td>
                          <td className="customer-name">
                            <strong>{order.customer_name}</strong>
                          </td>
                          <td>{formatAppDate(order.order_date)}</td>
                          <td className="text-right">
                            ${parseFloat(order.total_amount || 0).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </td>
                          <td>
                            <span className="status-badge status-info">{statusLabel('PACKED')}</span>
                          </td>
                          <td className="actions-cell">
                            <button
                              className="btn-action btn-sm"
                              onClick={() => handleStartShipping(order)}
                              title={t('shippingOperations.buttons.shipOrder')}
                            >
                              🚢 {t('shippingOperations.buttons.ship')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {shippingTab === 'shipped' && (
            <div className="shipped-list">
              <div className="list-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('shippingOperations.columns.orderNumber')}</th>
                      <th>{tCommon('ui.customer')}</th>
                      <th>{t('shippingOperations.columns.shipDate')}</th>
                      <th>{tCommon('ui.total')}</th>
                      <th>{tCommon('ui.status')}</th>
                      <th>{tCommon('ui.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shippedOrders.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="no-data">
                          {t('shippingOperations.empty.noShippedOrders')}
                        </td>
                      </tr>
                    ) : (
                      shippedOrders.map(order => (
                        <tr key={order.sales_order_id}>
                          <td className="customer-code">
                            <code>{order.so_number}</code>
                          </td>
                          <td className="customer-name">
                            <strong>{order.customer_name}</strong>
                          </td>
                          <td>{order.updated_at ? formatAppDate(order.updated_at) : tCommon('ui.notAvailable')}</td>
                          <td className="text-right">
                            ${parseFloat(order.total_amount || 0).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </td>
                          <td>
                            <span className="status-badge status-success">{statusLabel('SHIPPED')}</span>
                          </td>
                          <td className="actions-cell">
                            <button
                              className="btn-action btn-sm"
                              title={t('shippingOperations.buttons.createInvoice')}
                            >
                              📄 {t('shippingOperations.buttons.invoice')}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ─── Main render ──────────────────────────────────────────────────

  return (
    <div>
      {activeSubTab === 'picking' && renderPickLists()}
      {activeSubTab === 'packing' && renderPacking()}
      {activeSubTab === 'shipping' && renderSalesOrderShipping()}

      {/* Pick List Details Modal */}
      {showPickDetailsModal && selectedPickList && (
        <div className="modal-overlay" onClick={closePickDetailsModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>📝 {t('shippingOperations.pickDetails.title')}</h2>
              <button className="modal-close" onClick={closePickDetailsModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('shippingOperations.columns.pickListNumber')}:</span>
                <span className="info-value"><strong>{selectedPickList.pick_list_number}</strong></span>
                <span className="info-label">{t('shippingOperations.columns.productionOrder')}:</span>
                <span className="info-value"><strong>{selectedPickList.po_number}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{tCommon('ui.status')}:</span>
                <span className="info-value">
                  <span className={`status-badge ${
                    selectedPickList.status === 'PENDING' ? 'status-pending' :
                    selectedPickList.status === 'IN_PROGRESS' ? 'status-in-progress' :
                    selectedPickList.status === 'COMPLETED' ? 'status-success' :
                    'status-error'
                  }`}>{statusLabel(selectedPickList.status)}</span>
                </span>
                <span className="info-label">{tCommon('ui.priority')}:</span>
                <span className="info-value">
                  <span className={`status-badge ${
                    selectedPickList.priority === 'URGENT' ? 'status-error' :
                    selectedPickList.priority === 'HIGH' ? 'status-warning' :
                    'status-secondary'
                  }`}>{statusLabel(selectedPickList.priority)}</span>
                </span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('shippingOperations.columns.assignedTo')}:</span>
                <span className="info-value">{selectedPickList.assigned_to_name || '-'}</span>
                <span className="info-label">{t('shippingOperations.columns.created')}:</span>
                <span className="info-value">{formatAppDate(selectedPickList.created_date, 'datetime')}</span>
              </div>
            </div>

            <div className="modal-body">
              {loadingPickDetails ? (
                <div className="loading-state">{t('shippingOperations.loading.pickListItems')}</div>
              ) : !pickListDetails || !pickListDetails.items ? (
                <div className="no-data">
                  <p>{t('shippingOperations.empty.noPickListItems')}</p>
                </div>
              ) : (
                <div className="warehouse-table-container">
                  <table className="warehouse-table">
                    <thead>
                      <tr>
                        <th>{t('shippingOperations.columns.seq')}</th>
                        <th>{tCommon('ui.material')}</th>
                        <th>{t('shippingOperations.columns.fromLocation')}</th>
                        <th>{t('shippingOperations.columns.toLocation')}</th>
                        <th>{t('shippingOperations.columns.plannedQty')}</th>
                        <th>{t('shippingOperations.columns.pickedQty')}</th>
                        <th>{t('shippingOperations.columns.batch')}</th>
                        <th>{tCommon('ui.status')}</th>
                        {(selectedPickList.status === 'PENDING' || selectedPickList.status === 'IN_PROGRESS') && <th>{tCommon('ui.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {pickListDetails.items.map(item => {
                        let statusClass = 'status-pending';
                        if (item.status === 'PICKED') statusClass = 'status-success';
                        if (item.status === 'SHORT') statusClass = 'status-warning';

                        return (
                          <tr key={item.pick_item_id}>
                            <td>{item.pick_sequence}</td>
                            <td>
                              <strong>{item.item_code}</strong><br />
                              <span style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>{item.item_name}</span>
                            </td>
                            <td>{item.from_location}</td>
                            <td>{item.to_location}</td>
                            <td>{parseFloat(item.planned_quantity).toLocaleString()}</td>
                            <td>
                              {item.picked_quantity ? (
                                <strong style={{ color: parseFloat(item.picked_quantity) >= parseFloat(item.planned_quantity) ? 'var(--success)' : 'var(--warning)' }}>
                                  {parseFloat(item.picked_quantity).toLocaleString()}
                                </strong>
                              ) : '-'}
                            </td>
                            <td>{item.batch_number || '-'}</td>
                            <td><span className={`status-badge ${statusClass}`}>{statusLabel(item.status)}</span></td>
                            {(selectedPickList.status === 'PENDING' || selectedPickList.status === 'IN_PROGRESS') && (
                              <td>
                                <div className="pick-action-controls">
                                  {item.status === 'PENDING' && (
                                    <>
                                      <button className="pick-quick-btn success" onClick={() => handleQuickPick(item)}>
                                        ✓ {t('shippingOperations.buttons.pickAll')}
                                      </button>
                                      <button className="pick-quick-btn secondary" onClick={() => handleZeroPick(item)}>
                                        ✗ {t('shippingOperations.buttons.short')}
                                      </button>
                                    </>
                                  )}
                                  {item.status !== 'PENDING' && (
                                    <input
                                      type="number"
                                      className="pick-quantity-input"
                                      defaultValue={item.picked_quantity || ''}
                                      min="0"
                                      max={item.planned_quantity}
                                      step="0.01"
                                      placeholder={t('placeholders.quantity')}
                                      onBlur={(e) => {
                                        const newValue = parseFloat(e.target.value) || 0;
                                        if (newValue !== parseFloat(item.picked_quantity || 0)) {
                                          handleInlinePickRecord(item, newValue);
                                        }
                                      }}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          e.target.blur();
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {pickListDetails.notes && (
                    <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
                      <strong>{tCommon('ui.notes')}:</strong>
                      <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)' }}>{pickListDetails.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-action btn-secondary"
                onClick={closePickDetailsModal}
              >
                {tCommon('ui.close')}
              </button>
              {selectedPickList.status === 'IN_PROGRESS' &&
                pickListDetails?.items?.length > 0 &&
                pickListDetails.items.every(item => item.status === 'PICKED' || item.status === 'SHORT') && (
                <button
                  className="btn-action btn-success"
                  onClick={handleCompletePickList}
                  disabled={completingPickList}
                >
                  {completingPickList ? t('shippingOperations.buttons.completing') : `✅ ${t('shippingOperations.buttons.completePickList')}`}
                </button>
              )}
              {selectedPickList.status === 'COMPLETED' && (selectedPickList.issued_date || selectedPickList.issued_by) && (
                <div style={{ padding: '0.5rem 1rem', background: 'var(--success)', color: 'white', borderRadius: '4px', fontSize: '0.9em' }}>
                  ✓ {selectedPickList.issued_date
                    ? t('shippingOperations.pickDetails.issuedToProductionOn', { date: formatAppDate(selectedPickList.issued_date, 'datetime') })
                    : t('shippingOperations.pickDetails.issuedToProduction')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Packing Modal */}
      {showPackingModal && selectedPackingItem && (
        <div className="modal-overlay" onClick={handleClosePackingModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>📦 {t('shippingOperations.packModal.title')}</h2>
              <button className="modal-close" onClick={handleClosePackingModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('shippingOperations.columns.pickListNumber')}:</span>
                <span className="info-value"><strong>{selectedPackingItem.pick_list_number}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('shippingOperations.columns.orderReference')}:</span>
                <span className="info-value">{selectedPackingItem.po_number || selectedPackingItem.so_number || '-'}</span>
                <span className="info-label">{tCommon('ui.customer')}:</span>
                <span className="info-value">{selectedPackingItem.customer_name || '-'}</span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{tCommon('ui.status')}:</span>
                <span className="info-value">{statusLabel(selectedPackingItem.status)}</span>
                <span className="info-label">{t('shippingOperations.packModal.completed')}:</span>
                <span className="info-value">{selectedPackingItem.completion_time ? formatAppDate(selectedPackingItem.completion_time, 'datetime') : '-'}</span>
              </div>
            </div>

            <div className="modal-body">
              <div className="packing-instructions">
                <h3>{t('shippingOperations.packModal.instructionsTitle')}</h3>
                <p>{t('shippingOperations.packModal.instructions')}</p>

                <div className="packing-details">
                  <div className="detail-item">
                    <strong>{t('shippingOperations.packModal.totalItems')}:</strong> {selectedPackingItem.total_items || 0}
                  </div>
                  <div className="detail-item">
                    <strong>{t('shippingOperations.packModal.totalQuantity')}:</strong> {selectedPackingItem.total_quantity || 0}
                  </div>
                  <div className="detail-item">
                    <strong>{t('shippingOperations.packModal.pickListType')}:</strong> {selectedPackingItem.pick_list_type}
                  </div>
                </div>

                {!selectedPackingItem.packed_date && (
                  <div className="packing-actions">
                    <button
                      className="btn-action btn-success"
                      onClick={handleMarkAsPacked}
                      disabled={submittingPacking}
                      style={{ marginTop: '1rem' }}
                    >
                      {submittingPacking ? `⏳ ${t('shippingOperations.buttons.markingAsPacked')}` : `✅ ${t('shippingOperations.buttons.markAsPacked')}`}
                    </button>
                  </div>
                )}

                {selectedPackingItem.packed_date && (
                  <div className="packed-info">
                    <p style={{ color: 'var(--success)', fontWeight: 'bold' }}>
                      ✅ {t('shippingOperations.packModal.alreadyPackedOn', { date: formatAppDate(selectedPackingItem.packed_date, 'datetime') })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sales Order Packing Modal */}
      {showSalesOrderPackingModal && selectedSalesOrder && (
        <div className="modal-overlay" onClick={handleCloseSalesOrderPackingModal}>
          <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>📦 {t('shippingOperations.soPackModal.title')}</h2>
              <button className="modal-close" onClick={handleCloseSalesOrderPackingModal}>✕</button>
            </div>

            <div className="modal-material-info">
              <div className="material-info-row">
                <span className="info-label">{t('shippingOperations.columns.salesOrderNumber')}:</span>
                <span className="info-value"><strong>{selectedSalesOrder.so_number}</strong></span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{tCommon('ui.customer')}:</span>
                <span className="info-value">{selectedSalesOrder.customer_name || '-'}</span>
                <span className="info-label">{t('shippingOperations.soPackModal.orderValue')}:</span>
                <span className="info-value">${selectedSalesOrder.total_value ? parseFloat(selectedSalesOrder.total_value).toFixed(2) : '0.00'}</span>
              </div>
              <div className="material-info-row">
                <span className="info-label">{t('shippingOperations.soPackModal.currentStatus')}:</span>
                <span className="info-value">
                  <span className={`status-badge ${
                    selectedSalesOrder.so_status === 'PACKED' ? 'status-success' : 'status-warning'
                  }`}>
                    {selectedSalesOrder.so_status ? statusLabel(selectedSalesOrder.so_status) : tCommon('ui.unknown')}
                  </span>
                </span>
                <span className="info-label">{tCommon('ui.priority')}:</span>
                <span className="info-value">
                  <span className={`status-badge ${
                    selectedSalesOrder.priority === 'HIGH' ? 'status-error' :
                    selectedSalesOrder.priority === 'MEDIUM' ? 'status-warning' : 'status-inactive'
                  }`}>
                    {statusLabel(selectedSalesOrder.priority || 'NORMAL')}
                  </span>
                </span>
              </div>
            </div>

            <div className="modal-body">
              <div className="packing-instructions">
                <h3>{t('shippingOperations.soPackModal.packingStatus')}</h3>

                {/* Pick Lists Summary */}
                <div className="pick-lists-summary" style={{ marginBottom: '1.5rem' }}>
                  <h4>{t('shippingOperations.soPackModal.pickListsSummary')}</h4>
                  <div className="summary-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem',
                    marginBottom: '1rem'
                  }}>
                    {selectedSalesOrder.direct_pick_lists > 0 && (
                      <div className="summary-card" style={{
                        padding: '0.75rem',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        background: 'var(--background-secondary)'
                      }}>
                        <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9em' }}>{t('shippingOperations.soPackModal.directPickLists')}</h5>
                        <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                          {selectedSalesOrder.direct_completed}/{selectedSalesOrder.direct_pick_lists}
                        </div>
                        <div style={{ fontSize: '0.8em', color: 'var(--text-secondary)' }}>
                          {selectedSalesOrder.direct_completed === selectedSalesOrder.direct_pick_lists ? `✅ ${t('shippingOperations.soPackModal.complete')}` : `⏳ ${t('shippingOperations.soPackModal.inProgress')}`}
                        </div>
                      </div>
                    )}

                    {selectedSalesOrder.production_pick_lists > 0 && (
                      <div className="summary-card" style={{
                        padding: '0.75rem',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        background: 'var(--background-secondary)'
                      }}>
                        <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9em' }}>{t('shippingOperations.soPackModal.productionPickLists')}</h5>
                        <div style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                          {selectedSalesOrder.production_completed}/{selectedSalesOrder.production_pick_lists}
                        </div>
                        <div style={{ fontSize: '0.8em', color: 'var(--text-secondary)' }}>
                          {selectedSalesOrder.production_completed === selectedSalesOrder.production_pick_lists ? `✅ ${t('shippingOperations.soPackModal.complete')}` : `⏳ ${t('shippingOperations.soPackModal.inProgress')}`}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Overall Progress */}
                  <div className="overall-progress" style={{ marginTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 'bold' }}>{t('shippingOperations.soPackModal.overallProgress')}</span>
                      <span style={{ fontSize: '0.9em' }}>
                        {t('shippingOperations.soPackModal.pickListsCompletedRatio', {
                          completed: (selectedSalesOrder.direct_completed || 0) + (selectedSalesOrder.production_completed || 0),
                          total: selectedSalesOrder.total_pick_lists
                        })}
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      background: 'var(--border-color)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${selectedSalesOrder.total_pick_lists > 0 ?
                          (((selectedSalesOrder.direct_completed || 0) + (selectedSalesOrder.production_completed || 0)) / selectedSalesOrder.total_pick_lists) * 100 : 0}%`,
                        height: '100%',
                        background: ((selectedSalesOrder.direct_completed || 0) + (selectedSalesOrder.production_completed || 0)) === selectedSalesOrder.total_pick_lists ?
                          'var(--success)' : 'var(--warning)',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {selectedSalesOrder.so_status !== 'PACKED' &&
                 ((selectedSalesOrder.direct_completed || 0) + (selectedSalesOrder.production_completed || 0)) === selectedSalesOrder.total_pick_lists &&
                 selectedSalesOrder.total_pick_lists > 0 ? (
                  <div className="packing-actions">
                    <p style={{ color: 'var(--success)', fontWeight: 'bold', marginBottom: '1rem' }}>
                      ✅ {t('shippingOperations.soPackModal.allCompleted')}
                    </p>
                    <button
                      className="btn-action btn-success"
                      onClick={handlePackSalesOrder}
                      disabled={submittingPacking}
                      style={{ marginTop: '1rem' }}
                    >
                      {submittingPacking ? `⏳ ${t('shippingOperations.buttons.packingSalesOrder')}` : `✅ ${t('shippingOperations.buttons.packCompleteSalesOrder')}`}
                    </button>
                  </div>
                ) : selectedSalesOrder.so_status === 'PACKED' ? (
                  <div className="packed-info">
                    <p style={{ color: 'var(--success)', fontWeight: 'bold' }}>
                      ✅ {t('shippingOperations.soPackModal.alreadyPacked')}
                    </p>
                  </div>
                ) : (
                  <div className="not-ready-info">
                    <p style={{ color: 'var(--warning)', fontWeight: 'bold' }}>
                      ⏳ {t('shippingOperations.soPackModal.waitingForPickLists')}
                    </p>
                    <div style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                      {selectedSalesOrder.total_pick_lists === 0
                        ? t('shippingOperations.soPackModal.noPickLists')
                        : t('shippingOperations.soPackModal.pickListsCompletedOf', { completed: (selectedSalesOrder.direct_completed || 0) + (selectedSalesOrder.production_completed || 0), total: selectedSalesOrder.total_pick_lists })
                      }
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipping Modal */}
      {showShippingModal && selectedShippingOrder && (
        <div className="modal-overlay" onClick={handleCloseShippingModal}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('shippingOperations.shipModal.title')}</h3>
              <button className="modal-close" onClick={handleCloseShippingModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="shipping-details">
                <h4>{t('shippingOperations.shipModal.orderDetails')}</h4>
                <div className="order-info">
                  <div className="info-item">
                    <strong>{t('shippingOperations.shipModal.orderNumber')}:</strong> {selectedShippingOrder.so_number}
                  </div>
                  <div className="info-item">
                    <strong>{tCommon('ui.customer')}:</strong> {selectedShippingOrder.customer_name}
                  </div>
                  <div className="info-item">
                    <strong>{t('shippingOperations.shipModal.totalAmount')}:</strong> ${parseFloat(selectedShippingOrder.total_amount || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                </div>

                <h4>{t('shippingOperations.shipModal.shippingInformation')}</h4>
                <div className="form-group">
                  <label>{t('shippingOperations.shipModal.trackingNumber')}:</label>
                  <input
                    type="text"
                    value={shippingTrackingNumber}
                    onChange={(e) => setShippingTrackingNumber(e.target.value)}
                    placeholder={t('placeholders.trackingNumber')}
                  />
                </div>

                <div className="form-group">
                  <label>{t('shippingOperations.shipModal.carrier')}:</label>
                  <select
                    value={shippingCarrier}
                    onChange={(e) => setShippingCarrier(e.target.value)}
                  >
                    <option value="" disabled hidden>{t('shippingOperations.shipModal.selectCarrier')}</option>
                    <option value="UPS">UPS</option>
                    <option value="FedEx">FedEx</option>
                    <option value="DHL">DHL</option>
                    <option value="USPS">USPS</option>
                    <option value="Local Delivery">{t('shippingOperations.shipModal.carriers.localDelivery')}</option>
                    <option value="Customer Pickup">{t('shippingOperations.shipModal.carriers.customerPickup')}</option>
                    <option value="Other">{t('shippingOperations.shipModal.carriers.other')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>{t('shippingOperations.shipModal.shippingNotes')}:</label>
                  <textarea
                    value={shippingNotes}
                    onChange={(e) => setShippingNotes(e.target.value)}
                    placeholder={t('placeholders.shippingNotes')}
                    rows="3"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={handleCloseShippingModal}
                disabled={submittingShipping}
              >
                {tCommon('ui.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleShipOrder}
                disabled={submittingShipping}
              >
                {submittingShipping ? `⏳ ${t('shippingOperations.buttons.shipping')}` : `🚢 ${t('shippingOperations.buttons.shipOrder')}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ShippingOperations.displayName = 'ShippingOperations';

export default ShippingOperations;
